import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

interface NavbarProps {
  onAgendar: () => void;
}

const NAV_SECTIONS = [
  { label: 'Sobre', id: 'sobre' },
  { label: 'Atendimento', id: 'atendimento' },
  { label: 'Localização', id: 'localizacao' },
];

export default function Navbar({ onAgendar }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  const closeMobile = () => setMobileMenuOpen(false);

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    closeMobile();
    if (isHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  };

  const handleSectionClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    closeMobile();
    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(`/#${id}`);
    }
  };

  const navLinkClass = (active: boolean) =>
    `text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
      active ? 'text-[#C4A57B]' : 'text-[#8B7355] hover:text-[#6B5345]'
    }`;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
      <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16 py-1 sm:py-1.5">
        <div className="flex items-center justify-between">
          {/* Logo — H1 semântico na home para SEO */}
          {isHome ? (
            <h1 className="m-0 p-0 leading-none flex items-center">
              <Link to="/" onClick={handleLogoClick} className="flex items-center">
                <img
                  src="https://storage.readdy-site.link/project_files/4709dab1-64f1-4c72-8d6d-4bafa74c3cd9/59bbe872-63f2-45ce-8a90-da83127d51af_Laura-site.png?v=a35a41943875b3ac5eeade5feaf66f6e"
                  alt="Dra. Laura Folchini — Dermatologista em Criciúma e Tubarão SC"
                  className="h-16 sm:h-20 w-auto object-contain"
                />
              </Link>
            </h1>
          ) : (
            <Link to="/" onClick={handleLogoClick} className="flex items-center">
              <img
                src="https://storage.readdy-site.link/project_files/4709dab1-64f1-4c72-8d6d-4bafa74c3cd9/59bbe872-63f2-45ce-8a90-da83127d51af_Laura-site.png?v=a35a41943875b3ac5eeade5feaf66f6e"
                alt="Dra. Laura Folchini — Dermatologista"
                className="h-16 sm:h-20 w-auto object-contain"
              />
            </Link>
          )}

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_SECTIONS.map(({ label, id }) => (
              <a
                key={id}
                href={`/#${id}`}
                onClick={(e) => handleSectionClick(e, id)}
                className={navLinkClass(false)}
              >
                {label}
              </a>
            ))}
            <Link
              to="/tratamentos"
              className={navLinkClass(location.pathname === '/tratamentos')}
            >
              Tratamentos
            </Link>
            <Link
              to="/contato"
              className={navLinkClass(location.pathname === '/contato')}
            >
              Contato
            </Link>
            <button
              onClick={onAgendar}
              className="px-6 py-2.5 bg-[#C4A57B] text-white text-sm font-medium rounded-full hover:bg-[#B39568] transition-colors whitespace-nowrap cursor-pointer"
            >
              Agendar Consulta
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-[#8B7355] cursor-pointer"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            <i className={`text-2xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </div>

      {/* Mobile Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-[#E8DCC8] px-4 py-4 flex flex-col gap-4">
          {NAV_SECTIONS.map(({ label, id }) => (
            <a
              key={id}
              href={`/#${id}`}
              onClick={(e) => handleSectionClick(e, id)}
              className="text-sm font-medium text-[#8B7355] py-2 cursor-pointer"
            >
              {label}
            </a>
          ))}
          <Link
            to="/tratamentos"
            onClick={closeMobile}
            className={`text-sm font-medium py-2 cursor-pointer ${location.pathname === '/tratamentos' ? 'text-[#C4A57B]' : 'text-[#8B7355]'}`}
          >
            Tratamentos
          </Link>
          <Link
            to="/contato"
            onClick={closeMobile}
            className={`text-sm font-medium py-2 cursor-pointer ${location.pathname === '/contato' ? 'text-[#C4A57B]' : 'text-[#8B7355]'}`}
          >
            Contato
          </Link>
          <button
            onClick={() => { closeMobile(); onAgendar(); }}
            className="px-6 py-3 bg-[#C4A57B] text-white text-sm font-medium rounded-full text-center whitespace-nowrap cursor-pointer"
          >
            Agendar Consulta
          </button>
        </div>
      )}
    </nav>
  );
}
